import { useState, type ReactNode } from "react";
import { NavLink } from "react-router-dom";
import { ChevronRight, Home, MoreHorizontal } from "lucide-react";
import { cn } from "@/shared/lib/cn";

export type BreadcrumbItem = {
  label: ReactNode;
  to?: string | null;
  /** In-page navigation when `to` is not used (e.g. tree selection). */
  onClick?: () => void;
  /** Optional leading icon */
  icon?: ReactNode;
  /** Optional trailing badge */
  badge?: ReactNode;
  /** Force code pill style (e.g. for entity code like MO-001) */
  mono?: boolean;
};

export type BreadcrumbVariant = "default" | "pill" | "subtle";

export type BreadcrumbProps = {
  items: BreadcrumbItem[];
  className?: string;
  /** Custom separator between items (defaults to ChevronRight) */
  separator?: ReactNode;
  /** Show Home icon on the first item (auto-detected if label is Trang chủ / Ops) */
  showHomeIcon?: boolean;
  /** Visual variant */
  variant?: BreadcrumbVariant;
  /** Collapse intermediate items when items count exceeds maxItems */
  maxItems?: number;
  /** Transform all text labels to uppercase (defaults to true) */
  uppercase?: boolean;
};

const CODE_PATTERN_RE =
  /^(#|[A-Z]{2,5}-\d|LOT-|PO-|SO-|MO-|DM-|WO-|IQC-|FQC-|NCR-|PR-|GRN-)/i;

function isLikelyEntityCode(val: ReactNode): boolean {
  if (typeof val === "string") {
    const trimmed = val.trim();
    return CODE_PATTERN_RE.test(trimmed) || /^[A-Z0-9_-]{8,}$/.test(trimmed);
  }
  return false;
}

function isHomeLabel(val: ReactNode): boolean {
  if (typeof val === "string") {
    const lower = val.trim().toLowerCase();
    return lower === "trang chủ" || lower === "home" || lower === "ops";
  }
  return false;
}

/**
 * Modern Breadcrumb component for navigation hierarchy.
 * Features:
 * - Crisp ChevronRight separator
 * - Smooth hover pill interactions
 * - Smart code-badge formatting for entity IDs (e.g. MO-2026-001)
 * - Auto Home icon support
 * - Intermediate items collapse (maxItems)
 * - Accessible ARIA attributes
 */
export function Breadcrumb({
  items,
  className,
  separator,
  showHomeIcon,
  variant = "default",
  maxItems,
  uppercase = true,
}: BreadcrumbProps) {
  const [expanded, setExpanded] = useState(false);

  if (!items || items.length === 0) return null;

  const defaultSeparator = (
    <ChevronRight
      className="size-3.5 shrink-0 text-text-muted/60 transition-colors"
      aria-hidden="true"
    />
  );
  const activeSeparator = separator ?? defaultSeparator;

  const hasCollapse = Boolean(
    maxItems && maxItems > 2 && items.length > maxItems && !expanded,
  );

  const renderItemContent = (
    item: BreadcrumbItem,
    isLast: boolean,
    index: number,
  ) => {
    const isFirst = index === 0;
    const shouldDisplayHomeIcon =
      !item.icon &&
      isFirst &&
      (showHomeIcon || (showHomeIcon === undefined && isHomeLabel(item.label)));

    const leadingIcon = shouldDisplayHomeIcon ? (
      <Home
        className="size-3.5 shrink-0 text-text-muted transition-colors group-hover:text-primary"
        aria-hidden="true"
      />
    ) : item.icon ? (
      <span
        className="inline-flex size-3.5 shrink-0 items-center justify-center text-text-muted transition-colors group-hover:text-primary"
        aria-hidden="true"
      >
        {item.icon}
      </span>
    ) : null;

    const isCode = isLast && (item.mono || isLikelyEntityCode(item.label));

    const linkClassName =
      "group inline-flex items-center gap-1.5 rounded px-1.5 py-0.5 text-caption font-medium text-text-secondary transition-all duration-150 hover:bg-primary-soft/50 hover:text-primary focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--color-focus-ring)]";

    if (item.to && !isLast) {
      return (
        <NavLink to={item.to} className={linkClassName}>
          {leadingIcon}
          <span
            className={cn(
              "truncate group-hover:underline underline-offset-2",
              uppercase && "uppercase tracking-wide",
            )}
          >
            {item.label}
          </span>
          {item.badge}
        </NavLink>
      );
    }

    if (item.onClick && !isLast) {
      return (
        <button type="button" className={cn(linkClassName, "cursor-pointer")} onClick={item.onClick}>
          {leadingIcon}
          <span
            className={cn(
              "truncate group-hover:underline underline-offset-2",
              uppercase && "uppercase tracking-wide",
            )}
          >
            {item.label}
          </span>
          {item.badge}
        </button>
      );
    }

    if (isLast) {
      if (isCode) {
        return (
          <span
            className="inline-flex items-center gap-1.5 rounded border border-border/80 bg-surface px-2 py-0.5 font-mono text-[12px] font-bold text-text shadow-2xs"
            aria-current="page"
          >
            {leadingIcon}
            <span
              className={cn(
                "truncate",
                uppercase && "uppercase tracking-wide",
              )}
            >
              {item.label}
            </span>
            {item.badge}
          </span>
        );
      }

      return (
        <span
          className="inline-flex items-center gap-1.5 px-1.5 py-0.5 text-caption font-semibold text-text"
          aria-current="page"
        >
          {leadingIcon}
          <span
            className={cn(
              "truncate",
              uppercase && "uppercase tracking-wide",
            )}
          >
            {item.label}
          </span>
          {item.badge}
        </span>
      );
    }

    return (
      <span className="inline-flex items-center gap-1.5 px-1.5 py-0.5 text-caption font-medium text-text-secondary/80 select-none">
        {leadingIcon}
        <span
          className={cn(
            "truncate",
            uppercase && "uppercase tracking-wide",
          )}
        >
          {item.label}
        </span>
        {item.badge}
      </span>
    );
  };

  const containerClasses = cn(
    "text-caption text-text-secondary",
    uppercase && "uppercase tracking-wide",
    variant === "pill" &&
      "inline-flex items-center bg-surface border border-border/80 rounded-full px-3 py-1 shadow-2xs backdrop-blur-xs",
    variant === "subtle" &&
      "inline-flex items-center bg-bg/80 border border-border/60 rounded-control px-2.5 py-1",
    className,
  );

  if (hasCollapse) {
    const firstItem = items[0]!;
    const tailItems = items.slice(items.length - (maxItems! - 1));

    return (
      <nav aria-label="Đường dẫn" className={containerClasses}>
        <ol className="m-0 flex list-none flex-wrap items-center gap-1 p-0">
          <li className="inline-flex items-center gap-1">
            {renderItemContent(firstItem, false, 0)}
          </li>
          <li className="inline-flex items-center gap-1">
            {activeSeparator}
            <button
              type="button"
              onClick={() => setExpanded(true)}
              className="inline-flex size-6 cursor-pointer items-center justify-center rounded border border-border/60 bg-surface-muted/60 text-text-muted transition-colors hover:bg-surface-muted hover:text-text focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--color-focus-ring)]"
              title="Hiện tất cả cấp đường dẫn"
              aria-label="Hiện tất cả cấp đường dẫn"
            >
              <MoreHorizontal className="size-3.5" aria-hidden="true" />
            </button>
          </li>
          {tailItems.map((item, subIndex) => {
            const trueIndex = items.length - tailItems.length + subIndex;
            const isLast = trueIndex === items.length - 1;
            return (
              <li
                key={`${typeof item.label === "string" ? item.label : "sub"}-${trueIndex}`}
                className="inline-flex items-center gap-1"
              >
                {activeSeparator}
                {renderItemContent(item, isLast, trueIndex)}
              </li>
            );
          })}
        </ol>
      </nav>
    );
  }

  return (
    <nav aria-label="Đường dẫn" className={containerClasses}>
      <ol className="m-0 flex list-none flex-wrap items-center gap-1 p-0">
        {items.map((item, index) => {
          const isLast = index === items.length - 1;
          return (
            <li
              key={`${typeof item.label === "string" ? item.label : "item"}-${index}`}
              className="inline-flex items-center gap-1"
            >
              {index > 0 ? activeSeparator : null}
              {renderItemContent(item, isLast, index)}
            </li>
          );
        })}
      </ol>
    </nav>
  );
}
